import React, { useMemo } from "react";
import { parseSummary } from "./summary-helpers";
import { getLambdaSchema } from "./lambda-schemas";
import type { ColumnSpec } from "./lambda-schemas/types";
import { useToast } from "../../hooks/use-toast";
import vizConfigJson from "./viz-config.json";
const vizConfig: any = vizConfigJson;

// Friendly model name map for LLM call cards
const FRIENDLY_MODEL_NAME: Record<string, string> = {
  "anthropic:claude-sonnet-4-5-20250929": "Claude Sonnet 4.5",
  "anthropic:claude-3-7-sonnet-20250219": "Claude 3.7 Sonnet",
  "anthropic:claude-3-5-sonnet-20241022": "Claude 3.5 Sonnet",
  "openai:gpt-4o": "GPT 4o",
  "openai:gpt-5": "GPT 5",
};

type Props = {
  events: any[];
  sortMode?: "latency" | "chronological" | "chronologicalDesc";
  onOpenPatientTrace?: (patientId: string) => void;
  activeSection?: string; // optional section name from viz-config.tableSections
};

const ROW_HEIGHT = 28;
const LABEL_WIDTH = 220;

const toMs = (t: number | string): number => {
  if (typeof t === "number") return t;
  const n = Number(t);
  if (!Number.isNaN(n)) return n;
  const d = new Date(t as string).getTime();
  return Number.isNaN(d) ? 0 : d;
};

const toStringList = (val: unknown): string[] => {
  try {
    if (Array.isArray(val)) return val.map(String).map((s) => s.trim()).filter((s) => s.length > 0);
    if (typeof val === "string") {
      const s = val.trim();
      if (!s) return [];
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map(String).map((x) => x.trim()).filter((x) => x.length > 0);
      } catch {
        return [s];
      }
    }
    return [];
  } catch {
    return [];
  }
};

function formatSummaryCell(lambdaName: string, rawSummary: unknown): React.ReactNode {
  const s = parseSummary(lambdaName, rawSummary);
  if (!s || typeof s !== "object") return <span className="text-neutral-500">—</span>;
  const entries = Object.entries(s);
  if (entries.length === 0) return <span className="text-neutral-500">—</span>;
  return (
    <div className="text-xs text-neutral-800">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="text-neutral-500">{k}:</span>
          <span className="break-words">
            {Array.isArray(v) ? (v as unknown[]).join(", ") : String(v ?? "")}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PatientsTable({ events, sortMode = "latency", onOpenPatientTrace, activeSection }: Props) {
  const { toast } = useToast();
  const groups = useMemo(() => {
    const byPatient = new Map<string, any[]>();
    for (const ev of events || []) {
      const pid = String(ev?.patient_id ?? "");
      if (!pid) continue;
      if (!byPatient.has(pid)) byPatient.set(pid, []);
      byPatient.get(pid)!.push(ev);
    }
    // Sort events per patient by start time
    const sortedGroups: Array<{
      patient_id: string;
      rows: any[]; // filtered rows used for sorting and rendering
      earliestStart: number;
      maxSessionDuration: number;
    }> = [];
    // Support multiple session split lambdas
    const splitLambdaNames: Set<string> = new Set(
      Array.isArray((vizConfig as any)?.sessionSplitLambdas)
        ? ((vizConfig as any).sessionSplitLambdas as string[])
        : (typeof (vizConfig as any)?.sessionSplitLambda === "string" &&
          (vizConfig as any).sessionSplitLambda.trim().length > 0
            ? [(vizConfig as any).sessionSplitLambda]
            : [])
    );
    const suppressedSet = new Set<string>(
      Array.isArray(vizConfig?.suppressedLambdas) ? (vizConfig.suppressedLambdas as string[]) : []
    );
    const latencySortExcludedSet = new Set<string>(
      Array.isArray((vizConfig as any)?.latencySortExcludedLambdas)
        ? ((vizConfig as any).latencySortExcludedLambdas as string[])
        : []
    );
    for (const [pid, evs] of byPatient.entries()) {
      // Normalize and sort
      const normalized = evs
        .slice()
        .sort((a, b) => toMs(a.timestamp ?? a.start_time_ms) - toMs(b.timestamp ?? b.start_time_ms))
        .map((e) => {
          const start = toMs(e.timestamp ?? e.start_time_ms);
          const dur = toMs(e.time_taken_ms ?? 0);
          const end = start + Math.max(0, dur);
          return {
            lambda: e.artifact || e.lambda_name || "unknown",
            start,
            end,
            duration: Math.max(0, end - start),
            summary: e.summary,
            // errors/warnings removed in this view
          };
        });
      // If session split lambdas are configured, omit patients that lack an anchor entirely
      if (splitLambdaNames.size > 0) {
        const hasAnchor = normalized.some((r) => splitLambdaNames.has(r.lambda));
        if (!hasAnchor) {
          continue; // skip this patient
        }
      }
      // Apply suppression consistent with timeline: keep anchor lambda even if suppressed
      const rows = normalized.filter((r) => {
        if (splitLambdaNames.size > 0 && splitLambdaNames.has(r.lambda)) return true;
        return !suppressedSet.has(r.lambda);
      });
      // Fallback: if suppression removed everything, use normalized so patient still appears
      const effectiveRows = rows.length > 0 ? rows : normalized;
      const earliest = effectiveRows.length > 0 ? effectiveRows[0].start : Number.POSITIVE_INFINITY;
      // Compute longest session duration similar to timeline (split by sessionSplitLambdas starts)
      let maxSession = 0;
      if (effectiveRows.length > 0) {
        if (splitLambdaNames.size > 0) {
          const anchors = effectiveRows
            .filter((r) => splitLambdaNames.has(r.lambda))
            .map((r) => r.start)
            .sort((a, b) => a - b);
          if (anchors.length > 0) {
            const boundaries = [...anchors, Number.POSITIVE_INFINITY];
            for (let i = 0; i < anchors.length; i++) {
              const startTs = anchors[i];
              const endBoundary = boundaries[i + 1];
              const slice = effectiveRows.filter((r) => r.start >= startTs && r.start < endBoundary);
              const relevant = slice.filter((r) => !latencySortExcludedSet.has(r.lambda));
              if (relevant.length > 0) {
                const minStart = Math.min(...relevant.map((r) => r.start));
                const maxEnd = Math.max(...relevant.map((r) => r.end));
                const dur = Math.max(0, maxEnd - minStart);
                if (dur > maxSession) maxSession = dur;
              }
            }
          } else {
            // no anchors; fallback to overall span
            const relevant = effectiveRows.filter((r) => !latencySortExcludedSet.has(r.lambda));
            if (relevant.length > 0) {
              const minStart = Math.min(...relevant.map((r) => r.start));
              const maxEnd = Math.max(...relevant.map((r) => r.end));
              maxSession = Math.max(0, maxEnd - minStart);
            }
          }
        } else {
          // no split configured; use overall span
          const relevant = effectiveRows.filter((r) => !latencySortExcludedSet.has(r.lambda));
          if (relevant.length > 0) {
            const minStart = Math.min(...relevant.map((r) => r.start));
            const maxEnd = Math.max(...relevant.map((r) => r.end));
            maxSession = Math.max(0, maxEnd - minStart);
          }
        }
      }
      sortedGroups.push({ patient_id: pid, rows: effectiveRows, earliestStart: earliest, maxSessionDuration: maxSession });
    }
    // Sort patients
    if (sortMode === "chronological") {
      sortedGroups.sort((a, b) => a.earliestStart - b.earliestStart);
    } else if (sortMode === "chronologicalDesc") {
      // latest first based on earliestStart descending to match timeline's "firstStart" logic
      sortedGroups.sort((a, b) => b.earliestStart - a.earliestStart);
    } else {
      // latency: use longest session duration (desc)
      sortedGroups.sort((a, b) => b.maxSessionDuration - a.maxSessionDuration);
    }
    return sortedGroups;
  }, [events, sortMode]);

  // Read ordered lambdas for columns from viz-config.json, by section if provided
  const { lambdaOrder, sectionName } = useMemo(() => {
    const sections = Array.isArray((vizConfig as any)?.tableSections)
      ? ((vizConfig as any).tableSections as Array<{ name: string; lambdas: string[] }>)
      : [];
    if (sections.length > 0) {
      // choose section
      const sec =
        (sections.find((s) => s.name === (activeSection || "")) as
          | { name: string; lambdas: string[] }
          | undefined) || sections[0];
      return {
        lambdaOrder: Array.isArray(sec.lambdas) ? sec.lambdas : [],
        sectionName: sec.name,
      };
    }
    // fallback to legacy tableLambdaOrder if present
    const legacy = Array.isArray((vizConfig as any)?.tableLambdaOrder)
      ? ((vizConfig as any).tableLambdaOrder as string[])
      : [];
    return { lambdaOrder: legacy, sectionName: activeSection || "" };
  }, [activeSection]);

  // For each lambda: prefer schema-defined columns; otherwise derive union of keys
  const lambdaColumnsMap: Record<string, ColumnSpec[]> = useMemo(() => {
    const map: Record<string, ColumnSpec[]> = {};
    const seenMap: Record<string, Set<string>> = {};
    for (const ln of lambdaOrder) {
      const schema = getLambdaSchema(ln);
      if (schema && Array.isArray(schema.columns) && schema.columns.length > 0) {
        map[ln] = schema.columns.filter((c) => !c.hide);
        continue;
      }
      // derive
      map[ln] = [];
      seenMap[ln] = new Set<string>();
    }
    // only gather keys for lambdas without schemas
    for (const g of groups) {
      for (const r of g.rows) {
        const ln = r.lambda;
        if (!lambdaOrder.includes(ln)) continue;
        if (map[ln].length > 0) continue; // already schema-defined
        const obj = parseSummary(ln, r.summary) || {};
        for (const k of Object.keys(obj)) {
          if (!seenMap[ln].has(k)) {
            seenMap[ln].add(k);
            map[ln].push({ key: k, label: k });
          }
        }
      }
    }
    return map;
  }, [groups, lambdaOrder]);

  const renderValue = (col: ColumnSpec, v: unknown): React.ReactNode => {
    // Special handling: render llm_calls array as a compact table/list per-call
    if (col.key === "llm_calls" && Array.isArray(v)) {
      const arr = v as Array<any>;
      if (arr.length === 0) return <span className="text-neutral-500">—</span>;
      return (
        <div className="text-[11px] space-y-1">
          {arr.map((c, idx) => (
            <div
              key={idx}
              className={`relative flex flex-wrap gap-2 border rounded px-2 py-1 ${
                String(c?.status || "").toLowerCase() === "success"
                  ? "bg-neutral-50 border-neutral-200"
                  : "bg-amber-50 border-amber-300"
              }`}
            >
              <button
                type="button"
                className="absolute right-1 top-1 text-neutral-500 hover:text-neutral-800"
                title="View call JSON"
                onClick={(e) => {
                  e.stopPropagation();
                  const jsonStr = JSON.stringify(c, null, 2);
                  toast({
                    title: "LLM call",
                    description: (
                      <pre className="text-[11px] whitespace-pre-wrap break-all select-text">
                        {jsonStr}
                      </pre>
                    ),
                  });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </button>
              {c?.model ? (
                <span className="text-neutral-800 font-semibold">
                  {FRIENDLY_MODEL_NAME[String(c.model)] ?? String(c.model)}
                </span>
              ) : null}
              {/* Tokens line */}
              {(c?.input_tokens != null || c?.output_tokens != null) ? (
                <div className="w-full text-neutral-700">
                  <span className="text-neutral-500">tokens:</span>{" "}
                  {String(c.input_tokens ?? "—")} input, {String(c.output_tokens ?? "—")} output
                </div>
              ) : null}
              {/* Time line */}
              {(c?.time_taken_seconds != null || c?.time_take_seconds != null) ? (
                <div className="w-full text-neutral-700">
                  <span className="text-neutral-500">time:</span>{" "}
                  {Number(c.time_taken_seconds ?? c.time_take_seconds ?? 0).toFixed(2)} sec
                </div>
              ) : null}
              {String(c?.status || "").toLowerCase() !== "success" && c?.error_message ? (
                <div className="w-full text-amber-800">
                  <span className="text-neutral-500">error:</span> {String(c.error_message)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      );
    }
    if (col.transform === "joinComma" && Array.isArray(v)) {
      return (v as unknown[]).join(", ");
    }
    if (col.transform === "secondsFromMs") {
      const n = typeof v === "number" ? v : Number(v);
      if (Number.isFinite(n)) return `${(n / 1000).toFixed(3)} s`;
    }
    if (col.transform === "link") {
      const href = typeof v === "string" ? v : "";
      if (href) {
        return (
          <a href={href} target="_blank" rel="noreferrer" className="text-ktx-purple hover:underline break-all">
            {href}
          </a>
        );
      }
    }
    return String(v ?? "—");
  };

  return (
    <div className="w-full border rounded-lg overflow-hidden">
      <div className="overflow-auto">
        {/* Dark separators between lambda blocks */}
        {/*
          We add a strong right border on the last column of each lambda block (header and body)
          to clearly delineate lambda sections.
        */}
        <table className="w-full text-xs">
          <thead>
            <tr className="text-neutral-600 bg-neutral-50 align-bottom">
              <th
                className="text-left px-3 py-2 border-b border-r-2 border-neutral-800"
                style={{ width: 140 }}
                rowSpan={2}
              >
                Patient
              </th>
              {lambdaOrder.map((ln) => {
                const cols = lambdaColumnsMap[ln] || [];
                const span = Math.max(1, cols.length);
                return (
                  <th
                    key={`h-top-${ln}`}
                    className="text-left px-3 py-2 border-b border-r-2 border-neutral-800"
                    colSpan={span}
                  >
                    {ln}
                  </th>
                );
              })}
            </tr>
            <tr className="text-neutral-600 bg-neutral-50">
              {lambdaOrder.map((ln) => {
                const cols = lambdaColumnsMap[ln] || [];
                if (cols.length === 0) {
                  return (
                    <th
                      key={`h-sub-${ln}-empty`}
                      className="text-left px-3 py-2 border-b border-r-2 border-neutral-800"
                    >
                      Summary
                    </th>
                  );
                }
                return cols.map((c, idx) => {
                  const isLast = idx === cols.length - 1;
                  return (
                    <th
                      key={`h-sub-${ln}-${c.key}`}
                      className={`text-left px-3 py-2 border-b ${
                        isLast ? "border-r-2 border-neutral-800" : "border-r border-neutral-200"
                      } ${c.key === "llm_calls" ? "min-w-[300px]" : ""}`}
                    >
                      {c.label || c.key}
                    </th>
                  );
                });
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              // Align by row index across lambdas; use rowSpan for blanks to avoid repeated empty cells.
              const byLambdaList: Record<string, any[]> = {};
              for (const ln of lambdaOrder) byLambdaList[ln] = [];
              for (const r of g.rows) {
                const ln = r.lambda;
                if (!lambdaOrder.includes(ln)) continue;
                byLambdaList[ln].push(r);
              }
              const maxLines = Math.max(1, ...lambdaOrder.map((ln) => byLambdaList[ln].length || 0));
              const rows: React.ReactNode[] = [];
              const blankStartedAt: Record<string, number | null> = Object.fromEntries(
                lambdaOrder.map((ln) => [ln, null])
              ) as Record<string, number | null>;
              for (let i = 0; i < maxLines; i++) {
                const isLastRow = i === maxLines - 1;
                rows.push(
                  <tr
                    key={`row-${g.patient_id}-${i}`}
                    className={`${isLastRow ? "border-b-2 border-neutral-800" : "border-b border-neutral-200"} align-top`}
                  >
                    {i === 0 ? (
                      <td
                        className="px-3 py-2 text-ktx-purple border-r-2 border-neutral-800"
                        rowSpan={maxLines}
                        title={`Open trace for ${g.patient_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="leading-tight">
                            <button
                              type="button"
                              className="hover:underline text-left"
                              onClick={() => onOpenPatientTrace && onOpenPatientTrace(g.patient_id)}
                            >
                              {g.patient_id}
                            </button>
                            {(() => {
                              // Show local time when any sessionSplitLambda was triggered for this patient
                              const splitNames: Set<string> = new Set(
                                Array.isArray((vizConfig as any)?.sessionSplitLambdas)
                                  ? ((vizConfig as any).sessionSplitLambdas as string[])
                                  : (typeof (vizConfig as any)?.sessionSplitLambda === "string" &&
                                    (vizConfig as any).sessionSplitLambda.trim().length > 0
                                      ? [(vizConfig as any).sessionSplitLambda]
                                      : ["UploadConversationFunction"])
                              );
                              const rowsForPatient = (g.rows || []) as Array<{
                                lambda: string;
                                start: number;
                              }>;
                              const anchors = rowsForPatient
                                .filter((r) => splitNames.has(r.lambda))
                                .map((r) => r.start)
                                .sort((a, b) => a - b);
                              const firstAnchor = anchors.length > 0 ? anchors[0] : undefined;
                              return firstAnchor != null ? (
                                <div className="text-[10px] text-neutral-500">
                                  {new Date(firstAnchor).toLocaleTimeString()}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <button
                            type="button"
                            className="text-neutral-500 hover:text-neutral-800"
                            title="Copy patient id"
                            aria-label="Copy patient id"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(g.patient_id);
                              } catch {
                                const ta = document.createElement("textarea");
                                ta.value = g.patient_id;
                                document.body.appendChild(ta);
                                ta.select();
                                document.execCommand("copy");
                                document.body.removeChild(ta);
                              }
                              toast({
                                title: "Copied",
                                description: `Patient ID ${g.patient_id} copied`,
                              });
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    ) : null}
                    {lambdaOrder.map((ln) => {
                      const list = byLambdaList[ln];
                      const cols = lambdaColumnsMap[ln] || [];
                      const ev = list[i];
                      if (ev) {
                        const s = parseSummary(ln, ev.summary) || {};
                        if (cols.length === 0) {
                          const entries = Object.entries(s as Record<string, unknown>);
                          if (entries.length === 0) {
                            return (
                              <td
                                key={`cell-${g.patient_id}-${ln}-${i}`}
                                className="px-3 py-2 text-neutral-400 border-r-2 border-neutral-800"
                              />
                            );
                          }
                          return (
                            <td
                              key={`cell-${g.patient_id}-${ln}-${i}`}
                              className="px-3 py-2 text-neutral-800 border-r-2 border-neutral-800"
                            >
                              <div className="text-[11px]">
                                {entries.map(([k, v]) => (
                                  <div key={k} className="flex gap-1">
                                    <span className="text-neutral-500">{k}:</span>
                                    <span className="break-words">
                                      {Array.isArray(v) ? (v as unknown[]).join(", ") : String(v ?? "")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        }
                        return cols.map((c, idx) => {
                          const v = (s as any)[c.key];
                          const isLast = idx === cols.length - 1;
                          return (
                            <td
                              key={`cell-${g.patient_id}-${ln}-${c.key}-${i}`}
                      className={`px-3 py-2 text-neutral-800 ${
                                isLast ? "border-r-2 border-neutral-800" : "border-r border-neutral-200"
                      } ${c.key === "llm_calls" ? "min-w-[300px]" : ""}`}
                            >
                              <span className="break-words">{renderValue(c, v)}</span>
                            </td>
                          );
                        });
                      }
                      // No event at this row index: emit a single blank spanning remaining rows once
                      if (blankStartedAt[ln] == null) {
                        blankStartedAt[ln] = i;
                        const remaining = maxLines - i;
                        const colSpan = Math.max(1, cols.length);
                        return (
                          <td
                            key={`blank-span-${g.patient_id}-${ln}-from-${i}`}
                            className="px-3 py-2 border-r-2 border-neutral-800"
                            colSpan={colSpan}
                            rowSpan={remaining}
                          />
                        );
                      }
                      return null;
                    })}
                  </tr>
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

